import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Plus, X, ChevronUp, ChevronDown } from "lucide-react";
import { useApp } from "@/store/AppContext";
import { Button, Overline } from "@/components/ui-kit";
import { SAGE_PALETTE } from "@/lib/types";

const SUGGESTED = ["Personnel", "Travail", "Santé", "Famille", "Création", "Apprentissage"];

const Onboarding: React.FC = () => {
  const { addRole, removeRole, moveRole, addProject, completeOnboarding, data } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [roleInput, setRoleInput] = useState("");
  const [project, setProject] = useState("");

  const createRole = (label: string) => {
    if (!label.trim()) return;
    if (data.roles.some((r) => r.name.toLowerCase() === label.trim().toLowerCase())) return;
    const color = SAGE_PALETTE[data.roles.length % SAGE_PALETTE.length];
    addRole(label, color);
    setRoleInput("");
  };

  const finish = () => {
    if (project.trim()) {
      const roleId = data.roles[0]?.id ?? null;
      addProject(project, roleId);
    }
    completeOnboarding(name || "toi");
    navigate("/today", { replace: true });
  };

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div className="mx-auto w-full max-w-md min-h-screen bg-cream flex flex-col px-7 pt-16 pb-10 shadow-[0_0_80px_rgba(33,38,33,0.08)]">
      {/* progress dots */}
      <div className="flex items-center gap-2 mb-14">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === step ? "w-8 bg-sage" : i < step ? "w-4 bg-sage/40" : "w-4 bg-forest-900/10"
            }`}
          />
        ))}
      </div>

      <div className="flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {step === 0 && (
              <div>
                <Overline className="mb-4">Bienvenue sur Alinéa</Overline>
                <h1 className="font-heading text-4xl font-light tracking-tight text-forest-900 leading-tight mb-3">
                  Un espace calme pour avancer avec intention.
                </h1>
                <p className="text-forest-500 leading-relaxed mb-10">
                  Pas de statistiques, pas de bruit. Juste toi, tes projets et une chose à la fois.
                </p>
                <label className="block">
                  <span className="text-sm text-forest-700 mb-2 block">Comment devons-nous t'appeler&nbsp;?</span>
                  <input
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && next()}
                    data-testid="onboarding-name-input"
                    placeholder="Ton prénom"
                    className="w-full bg-white border border-forest-900/10 rounded-2xl px-5 py-4 text-forest-900 placeholder:text-forest-500/50 focus:outline-none focus:ring-2 focus:ring-sage/40"
                  />
                </label>
              </div>
            )}

            {step === 1 && (
              <div>
                <Overline className="mb-4">Étape 1 · Tes rôles</Overline>
                <h1 className="font-heading text-4xl font-light tracking-tight text-forest-900 leading-tight mb-3">
                  Quels sont tes domaines de vie&nbsp;?
                </h1>
                <p className="text-forest-500 leading-relaxed mb-8">
                  Tes rôles regroupent tes projets. Choisis-en quelques-uns.
                </p>

                <div className="flex flex-wrap gap-2.5 mb-6">
                  {SUGGESTED.map((s) => {
                    const active = data.roles.some((r) => r.name === s);
                    return (
                      <button
                        key={s}
                        onClick={() => !active && createRole(s)}
                        disabled={active}
                        data-testid={`suggested-role-${s}`}
                        className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-all ${
                          active
                            ? "bg-sage text-white border-sage"
                            : "bg-white text-forest-700 border-forest-900/10 hover:border-sage/50"
                        }`}
                      >
                        {active && <Check className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />}
                        {s}
                      </button>
                    );
                  })}
                </div>

                <div className="flex gap-2">
                  <input
                    value={roleInput}
                    onChange={(e) => setRoleInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && createRole(roleInput)}
                    data-testid="onboarding-role-input"
                    placeholder="Ajouter un rôle…"
                    className="flex-1 bg-white border border-forest-900/10 rounded-2xl px-5 py-3.5 text-forest-900 placeholder:text-forest-500/50 focus:outline-none focus:ring-2 focus:ring-sage/40"
                  />
                  <button
                    onClick={() => createRole(roleInput)}
                    data-testid="onboarding-add-role"
                    className="h-[52px] w-[52px] rounded-2xl bg-sage text-white flex items-center justify-center shrink-0"
                    aria-label="Ajouter le rôle"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>

                {data.roles.length > 0 && (
                  <div className="mt-6">
                    <p className="text-xs uppercase tracking-[0.18em] text-forest-500 mb-3">
                      Tes rôles ({data.roles.length}) · réorganise l'ordre
                    </p>
                    <div className="space-y-2" data-testid="added-roles-list">
                      {data.roles.map((r, i) => (
                        <div
                          key={r.id}
                          data-testid={`added-role-${r.id}`}
                          className="flex items-center gap-3 pl-3 pr-2 py-2.5 rounded-2xl bg-white border border-forest-900/10"
                        >
                          <div className="flex flex-col -my-1 shrink-0">
                            <button
                              onClick={() => moveRole(r.id, "up")}
                              disabled={i === 0}
                              data-testid={`move-role-up-${r.id}`}
                              aria-label="Monter le rôle"
                              className="h-5 w-5 flex items-center justify-center rounded-md text-forest-500 hover:bg-forest-900/5 disabled:opacity-25 disabled:hover:bg-transparent"
                            >
                              <ChevronUp className="h-4 w-4" strokeWidth={2} />
                            </button>
                            <button
                              onClick={() => moveRole(r.id, "down")}
                              disabled={i === data.roles.length - 1}
                              data-testid={`move-role-down-${r.id}`}
                              aria-label="Descendre le rôle"
                              className="h-5 w-5 flex items-center justify-center rounded-md text-forest-500 hover:bg-forest-900/5 disabled:opacity-25 disabled:hover:bg-transparent"
                            >
                              <ChevronDown className="h-4 w-4" strokeWidth={2} />
                            </button>
                          </div>
                          <span
                            className="h-3.5 w-3.5 rounded-full shrink-0"
                            style={{ backgroundColor: r.color }}
                          />
                          <span className="flex-1 min-w-0 truncate text-sm text-forest-800">
                            {r.name}
                          </span>
                          <button
                            onClick={() => removeRole(r.id)}
                            data-testid={`remove-role-${r.id}`}
                            aria-label={`Retirer ${r.name}`}
                            className="h-7 w-7 rounded-full flex items-center justify-center text-forest-500 hover:bg-forest-900/5 shrink-0"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div>
                <Overline className="mb-4">Étape 2 · Ton premier projet</Overline>
                <h1 className="font-heading text-4xl font-light tracking-tight text-forest-900 leading-tight mb-3">
                  Sur quoi veux-tu avancer&nbsp;?
                </h1>
                <p className="text-forest-500 leading-relaxed mb-8">
                  Un seul projet suffit pour commencer. Tu pourras en ajouter d'autres.
                </p>
                <input
                  autoFocus
                  value={project}
                  onChange={(e) => setProject(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && next()}
                  data-testid="onboarding-project-input"
                  placeholder="Ex. Écrire mon roman"
                  className="w-full bg-white border border-forest-900/10 rounded-2xl px-5 py-4 text-forest-900 placeholder:text-forest-500/50 focus:outline-none focus:ring-2 focus:ring-sage/40"
                />
                {data.roles[0] && project.trim() && (
                  <p className="mt-4 text-sm text-forest-500">
                    Rattaché à <span className="text-forest-800 font-medium">{data.roles[0].name}</span>
                  </p>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="pt-6">
                <div className="h-20 w-20 rounded-full bg-sage/15 flex items-center justify-center mb-8">
                  <Check strokeWidth={1.5} className="h-9 w-9 text-sage-dark" />
                </div>
                <h1 className="font-heading text-4xl font-light tracking-tight text-forest-900 leading-tight mb-3">
                  Tout est prêt{name ? `, ${name}` : ""}.
                </h1>
                <p className="text-forest-500 leading-relaxed">
                  Chaque écran ne pose qu'une seule question. Commence par définir ton intention du jour.
                </p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center gap-3 pt-8">
        {step > 0 && (
          <button
            onClick={back}
            data-testid="onboarding-back"
            className="h-14 w-14 rounded-full bg-white border border-forest-900/10 flex items-center justify-center text-forest-700 shrink-0"
            aria-label="Précédent"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        {step < 3 ? (
          <Button
            full
            onClick={next}
            disabled={step === 0 && !name.trim()}
            data-testid="onboarding-next"
          >
            Continuer
            <ArrowRight className="h-5 w-5" />
          </Button>
        ) : (
          <Button full onClick={finish} data-testid="onboarding-finish">
            Entrer dans Alinéa
            <ArrowRight className="h-5 w-5" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
